// src/hooks/useTemplates.tsx
import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { RootState } from "../../../store";
import { medicalsApi } from "../../../api/medicalsApi";
import {
  setDetailedTemplates,
  setSoapTemplates,
} from "../../../slices/template";
import {
  NotificationVariant,
  useNotifications,
} from "../../../components/dashboard/NotificationsProvider";
import useMedicalEncounter from "../../../hooks/useMedicalEncounter";
import useRoles from "../../../hooks/useRoles";
import {
  hasViewAccessByTemplates,
  verifyInteractivity,
} from "../../../utils/templateUtils";
import {
  EncounterTemplate,
  EncounterTemplateDefinition,
} from "../../../types/templates";
import { TInteractivity } from "../../../types/interactivity";

const useTemplates = () => {
  const soapTemplates = useSelector(
    (state: RootState) => state.templates.soapTemplates,
  );
  const detailedTemplates = useSelector(
    (state: RootState) => state.templates.detailedTemplates,
  );
  const preferredSimpleTemplate = useSelector(
    (state: RootState) => state.templates.preferredSimpleTemplate,
  );
  const preferredDetailedTemplate = useSelector(
    (state: RootState) => state.templates.preferredDetailedTemplate,
  );
  const dispatch = useDispatch();
  const { setNotification } = useNotifications();
  const { allowEncounterTemplate } = useFlags();
  const { currentTemplate } = useMedicalEncounter();
  const userRoles = useRoles();

  // allows for async await in app outside of redux middleware
  const fetchTemplates = useCallback(async () => {
    try {
      const templates = await medicalsApi.getTemplates({
        type: "Encounter",
        limit: "0",
      });

      if (templates?.length > 0) {
        const detailed = templates.filter(
          (v) => v.definition.encounterType === "detailed",
        );
        const simple = templates.filter(
          (v) => v.definition.encounterType === "simple",
        );

        dispatch(setDetailedTemplates(detailed));
        dispatch(setSoapTemplates(simple));
      }
    } catch (e) {
      setNotification({
        message: `An error occurred while fetching templates. Please refresh the page and try again.`,
        variant: NotificationVariant.error,
      });
    }
  }, [dispatch, setNotification]);

  const hasViewAccess = useCallback(
    (
      key: keyof EncounterTemplateDefinition,
      tem: EncounterTemplate = currentTemplate,
    ) => {
      const template = tem || [
        ...(detailedTemplates || []),
        ...(soapTemplates || []),
      ];
      if (!allowEncounterTemplate) return true;
      return hasViewAccessByTemplates(template, key, userRoles);
    },
    [
      allowEncounterTemplate,
      currentTemplate,
      detailedTemplates,
      soapTemplates,
      userRoles,
    ],
  );

  const hasEditAccess = useCallback(
    (
      key: keyof EncounterTemplateDefinition,
      interactivity: TInteractivity = "full",
      tem: EncounterTemplate = currentTemplate,
    ) => {
      if (!allowEncounterTemplate) return true;
      const verifiedInteractivity = verifyInteractivity(
        interactivity,
        tem || [...(detailedTemplates || []), ...(soapTemplates || [])],
        key,
        userRoles,
      );

      // return true if it's full
      return ["full"].includes(verifiedInteractivity);
    },
    [
      allowEncounterTemplate,
      currentTemplate,
      userRoles,
      detailedTemplates,
      soapTemplates,
    ],
  );

  const getAccessInteractivity = useCallback(
    (key: keyof EncounterTemplateDefinition, interactivity: TInteractivity) => {
      if (!allowEncounterTemplate) return interactivity;
      const verifiedInteractivity = verifyInteractivity(
        interactivity,
        currentTemplate || [
          ...(detailedTemplates || []),
          ...(soapTemplates || []),
        ],
        key,
        userRoles,
      );
      return verifiedInteractivity;
    },
    [
      allowEncounterTemplate,
      currentTemplate,
      userRoles,
      detailedTemplates,
      soapTemplates,
    ],
  );

  const hasACLRoleInTemplates = useCallback(
    (role: string) => {
      if (
        Array.isArray(currentTemplate?.acl) &&
        currentTemplate?.acl?.some((p) => p.roles.includes(role))
      ) {
        return true;
      }

      if (
        Array.isArray(detailedTemplates) &&
        detailedTemplates?.some((p) =>
          p.acl?.some((v) => v.roles.includes(role)),
        )
      ) {
        return true;
      }

      if (
        Array.isArray(soapTemplates) &&
        soapTemplates?.some((p) => p.acl?.some((v) => v.roles.includes(role)))
      ) {
        return true;
      }

      return false;
    },
    [currentTemplate, detailedTemplates, soapTemplates],
  );

  return {
    soapTemplates,
    detailedTemplates,
    preferredSimpleTemplate,
    preferredDetailedTemplate,
    fetchTemplates,
    hasViewAccess,
    hasEditAccess,
    getAccessInteractivity,
    hasACLRoleInTemplates,
  };
};

export default useTemplates;
